import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getMe, type MeResponse } from '../api/meApi'

import {
  selectAccessToken,
  selectIsAuthenticated,
  selectUser,
  setPermissions,
  setUser,
} from '@/store/slices/authSlice'

/**
 * Build the cache key for the current admin's `/admin/me` query. Including
 * `userId` is critical: it scopes the cache per admin so a same-tab login as
 * a different user can never read the previous admin's response.
 */
export const meQueryKey = (userId: string | undefined) => ['me', userId] as const

/** @deprecated kept for any legacy invalidate sites — prefer meQueryKey(userId). */
export const ME_QUERY_KEY = ['me'] as const

/**
 * Live-sync hook for the authenticated admin's profile + effective permissions.
 *
 * Mount once at the top of the authenticated layout (MainLayout). On every
 * successful fetch the hook pushes the latest `permissions` and
 * `is_super_admin` into Redux via `setPermissions`, so all existing
 * `useSelector(selectPermissions)` call sites (Sidebar, ProtectedRoute, &lt;Can&gt;)
 * react in real time without any extra wiring.
 *
 * Fetch policy:
 *   - `enabled` is gated on isAuthenticated so we never fire before login.
 *   - 60s polling keeps an idle tab within one refresh of the source of truth.
 *     The endpoint is Redis-cached server-side, so the real cost is a JWT check
 *     plus a Redis GET — cheap enough for this cadence.
 *   - `refetchOnWindowFocus` covers the common case of the admin coming back
 *     to the tab after a super-admin edited their group.
 *   - `staleTime: 30_000` suppresses accidental double-fetches from remounts.
 *   - `retry: false` — 401s and 403s should drop through to the axios
 *     interceptor / error boundary, not be papered over with retries.
 *
 * Mutation sites (RBAC group edits, assignment changes) should call
 * `queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })` after a
 * successful write to pull the fresh permission set immediately.
 */
export const useMe = () => {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const accessToken = useSelector(selectAccessToken)
  const user = useSelector(selectUser)

  const query = useQuery<MeResponse>({
    // Scope cache by user id — switching admins MUST switch the cache slot
    // so we never serve admin2's response for an admin3 session.
    queryKey: meQueryKey(user?.id),
    queryFn: getMe,
    // Gate on the token rather than userId so the very first fetch (right
    // after login, before user.id is set) still fires. The userId in the key
    // updates as soon as Redux settles; react-query swaps to a fresh slot.
    enabled: isAuthenticated && !!accessToken,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  })

  useEffect(() => {
    if (!query.data) return
    const me = query.data

    dispatch(
      setPermissions({
        permissions: me.permissions ?? [],
        isSuperAdmin: !!me.is_super_admin,
      }),
    )

    // Refresh the profile fields too so edits to email / full_name done from
    // the admin-management screen land on the top-bar avatar without a
    // re-login. Only dispatch when something actually changed to avoid
    // needless Redux churn (and accompanying re-renders of every consumer).
    if (!user) return
    // Build the next user payload from the response (authoritative) instead
    // of spreading the current Redux user — the spread can leak fields from a
    // previous admin if Redux + react-query are momentarily out of sync.
    const nextFullName = me.full_name ?? me.username ?? user.fullName
    const nextEmail = me.email ?? ''
    const nextPhone = me.phone_number ?? user.phoneNumber ?? ''
    const nextUsername = me.username ?? user.username
    if (
      user.fullName !== nextFullName ||
      user.email !== nextEmail ||
      user.phoneNumber !== nextPhone ||
      user.username !== nextUsername
    ) {
      dispatch(
        setUser({
          id: user.id,
          username: nextUsername,
          fullName: nextFullName,
          email: nextEmail,
          phoneNumber: nextPhone,
          avatar: user.avatar,
          roles: user.roles,
          permissions: user.permissions,
          isSuperAdmin: user.isSuperAdmin,
        }),
      )
    }
  }, [query.data, dispatch, user])

  return query
}

export default useMe
